class CartRewardProgress extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    this.messageEl = this.querySelector('[data-reward-message]');
    this.meterEl = this.querySelector('[data-reward-meter]');
    this.nodeEls = Array.from(this.querySelectorAll('[data-reward-node]'));
    this.config = window.cart_reward_progress || {};
    this.nodes = this.getSortedNodes();
    this.itemCount = parseInt(this.dataset.cartItemCount, 10) || 0;

    this.onCartChange = this.onCartChange.bind(this);
    this.onCartUpdateEvent = this.onCartUpdateEvent.bind(this);

    this.addEventListener('change', this.onCartChange);
    document.addEventListener('cart-update', this.onCartUpdateEvent);

    const initialSubtotal = parseInt(this.dataset.cartSubtotal, 10);
    if (!Number.isNaN(initialSubtotal)) {
      this.updateProgress(initialSubtotal / 100, this.itemCount);
    }

    this.fetchCartAndUpdate();
  }

  disconnectedCallback() {
    document.removeEventListener('cart-update', this.onCartUpdateEvent);
  }

  getSortedNodes() {
    const fromDom = this.nodeEls
      .map((el) => ({
        threshold: parseFloat(el.dataset.threshold),
        label: el.dataset.label || '',
        message: el.dataset.message || el.dataset.label || '',
        el,
      }))
      .filter((node) => !Number.isNaN(node.threshold) && node.label);

    fromDom.sort((a, b) => a.threshold - b.threshold);
    return fromDom;
  }

  getProgressAmount(cart) {
    const cents = cart.original_total_price ?? cart.items_subtotal_price ?? cart.total_price ?? 0;
    return parseInt(cents, 10) / 100;
  }

  isNodeReached(node, cartTotal, itemCount) {
    if (node.threshold === 0) {
      return itemCount > 0;
    }
    return cartTotal >= node.threshold;
  }

  getCartUrl() {
    const root = (window.routes && window.routes.root) ? window.routes.root : '';
    return `${root}/cart.js`;
  }

  fetchCartAndUpdate() {
    if (typeof Shopify !== 'undefined' && typeof Shopify.getCart === 'function') {
      Shopify.getCart((cart) => {
        this.applyCart(cart);
      });
      return;
    }

    fetch(this.getCartUrl(), { credentials: 'same-origin' })
      .then((res) => res.json())
      .then((cart) => this.applyCart(cart))
      .catch(() => {});
  }

  applyCart(cart) {
    if (!cart) return;

    const cartTotal = this.getProgressAmount(cart);
    const itemCount = cart.item_count || 0;

    this.dataset.cartSubtotal = String(cart.original_total_price || cart.items_subtotal_price || 0);
    this.dataset.cartItemCount = String(itemCount);
    this.itemCount = itemCount;

    this.updateProgress(cartTotal, itemCount);
  }

  onCartChange() {
    this.fetchCartAndUpdate();
  }

  onCartUpdateEvent(event) {
    if (event && event.detail) {
      this.applyCart(event.detail);
      return;
    }
    this.fetchCartAndUpdate();
  }

  updateProgress(cartTotal, itemCount) {
    if (!this.nodes.length) {
      this.style.display = 'none';
      return;
    }

    this.style.display = '';

    const maxThreshold = Math.max(...this.nodes.map((node) => node.threshold), 0);
    const progressPercent = maxThreshold > 0
      ? Math.min(100, Math.max(0, (cartTotal / maxThreshold) * 100))
      : (itemCount > 0 ? 100 : 0);

    this.positionNodes(maxThreshold);
    this.updateNodesState(cartTotal, itemCount);
    this.updateMeter(progressPercent);
    this.updateMessage(cartTotal, itemCount);
  }

  positionNodes(maxThreshold) {
    const count = this.nodes.length;

    this.nodes.forEach((node, index) => {
      let left = 0;
      if (maxThreshold > 0) {
        left = (node.threshold / maxThreshold) * 100;
      } else if (count > 1) {
        left = (index / (count - 1)) * 100;
      } else {
        left = 100;
      }
      node.el.style.left = `${left}%`;
    });
  }

  updateNodesState(cartTotal, itemCount) {
    let nextFound = false;

    this.nodes.forEach((node) => {
      node.el.classList.remove('is-reached', 'is-next');

      if (this.isNodeReached(node, cartTotal, itemCount)) {
        node.el.classList.add('is-reached');
      } else if (!nextFound) {
        node.el.classList.add('is-next');
        nextFound = true;
      }
    });
  }

  updateMeter(progressPercent) {
    if (!this.meterEl) return;
    this.meterEl.style.width = `${Math.min(100, Math.max(0, progressPercent))}%`;
  }

  applyTemplate(template, amountHtml, labelHtml) {
    return String(template || '')
      .replace(/\{\{\s*amount\s*\}\}/g, amountHtml)
      .replace(/\{\{\s*label\s*\}\}/g, labelHtml);
  }

  updateMessage(cartTotal, itemCount) {
    if (!this.messageEl) return;

    const nextNode = this.nodes.find((node) => !this.isNodeReached(node, cartTotal, itemCount));
    const completeTemplate = this.config.msg_complete || "You've unlocked all goals!";
    const emptyTemplate = this.config.msg_empty || this.config.msg_remaining || '{{ amount }} away from {{ label }}';
    const remainingTemplate = this.config.msg_remaining || '{{ amount }} away from {{ label }}';

    if (!nextNode) {
      this.messageEl.textContent = '';
      this.messageEl.innerHTML = completeTemplate;
      this.messageEl.classList.add('is-complete');
      this.messageEl.classList.remove('is-empty');
      return;
    }

    this.messageEl.classList.remove('is-complete');
    const remaining = Math.max(0, nextNode.threshold - cartTotal);
    const amountHtml = `<span class="cartRewardProgress-amount">${this.formatMoney(remaining)}</span>`;
    const labelHtml = `<span class="cartRewardProgress-target">${this.escapeHtml(nextNode.message || nextNode.label)}</span>`;
    const template = cartTotal <= 0 ? emptyTemplate : remainingTemplate;

    this.messageEl.classList.toggle('is-empty', cartTotal <= 0);
    this.messageEl.innerHTML = this.applyTemplate(template, amountHtml, labelHtml);

    if (
      (window.show_multiple_currencies && typeof Currency !== 'undefined' && Currency.currentCurrency != shopCurrency)
      || window.show_auto_currency
    ) {
      Currency.convertAll(
        window.shop_currency,
        $('#currencies .active').attr('data-currency'),
        'span.money',
        'money_format'
      );
    }
  }

  formatMoney(amount) {
    const cents = Math.round(amount * 100);
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      const moneyFormat = window.money_format || '{{amount}}';
      return Shopify.formatMoney(cents, moneyFormat);
    }
    return `$${amount.toFixed(2)}`;
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (!customElements.get('cart-reward-progress')) {
  customElements.define('cart-reward-progress', CartRewardProgress);
}
